/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useEffect, forwardRef } from 'react';
import { css, jsx } from '@emotion/react';
import { Container } from './container';
import { Background } from './background';
import { animated, useTransition } from '@react-spring/web';
import { DomPortal } from '../DomPortal';

const concealedCss = css`
	visibility: hidden;
	pointer-events: none;
`;

export interface ModalProps {
	isShowing: boolean;
	toggle: () => void;
	padding?: number;
	minWidth?: string;
	minHeight?: string;
	maxWidth?: string;
	maxHeight?: string;
	isDismissable?: boolean;
	disableClickOutside?: boolean;
	isFullSize?: boolean;
	isFullWidth?: boolean;
	isFullHeight?: boolean;
	isMaximizable?: boolean;
	overflow?: string;
	children?: React.ReactNode;
	style?: React.CSSProperties;
	/** Keeps the modal mounted, but hidden, so an overlay can open without dropping its state. */
	concealed?: boolean;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
	(
		{
			isShowing,
			toggle,
			children,
			minWidth,
			minHeight,
			maxWidth,
			maxHeight,
			padding = 1,
			isDismissable = true,
			disableClickOutside = false,
			isFullSize = false,
			isFullWidth = false,
			isFullHeight = false,
			isMaximizable = false,
			overflow,
			style,
			concealed = false,
		},
		forwardRef
	) => {
		const fadeInOutTransition = useTransition(isShowing, {
			from: { opacity: 0 },
			enter: { opacity: 1 },
			leave: { opacity: 0 },
			reverse: isShowing,
			config: { duration: 100 },
		});

		useEffect(() => {
			const body = document.querySelector('body');
			body!.style.overflowY = isShowing ? 'hidden' : 'auto';

			return () => {
				if (isShowing) {
					body!.style.overflowY = 'auto';
				}
			};
		}, [isShowing]);

		return fadeInOutTransition(
			(styles, modal) =>
				modal && (
					<DomPortal selector={'body'}>
						<animated.div style={styles}>
							<div css={concealed ? concealedCss : undefined} aria-hidden={concealed}>
								<Background onClick={() => (isDismissable && !disableClickOutside ? toggle() : undefined)} />
								<Container
									ref={forwardRef}
									padding={padding}
									toggle={toggle}
									minWidth={minWidth}
									minHeight={minHeight}
									maxWidth={maxWidth}
									maxHeight={maxHeight}
									isDismissable={isDismissable}
									isFullSize={isFullSize}
									isFullWidth={isFullWidth}
									isFullHeight={isFullHeight}
									isMaximizable={isMaximizable}
									overflow={overflow}
									style={style}
								>
									{children}
								</Container>
							</div>
						</animated.div>
					</DomPortal>
				)
		);
	}
);
